import { useCallback, useEffect, useRef } from "react";
import type {
	HostToClientMessage,
	MessagePayload,
} from "@rubyplay/effw-connector";
import { useCommunicator } from "@/contexts/CommunicatorContext";
import { debugLog } from "@/utils/debug";
import { useSubscription } from "@/hooks/useSubscription";
import {
	type BetPayload,
	parseBetPayload,
	parseBetPayloadFromBetUpdated,
	parseBetPayloadFromConfigConnection,
} from "@/shared/parsers/betPayload";
import type {
	EngagementProvider,
	EngagementProviderEvent,
} from "@/provider/EngagementProvider";

const EFW_CHEAT_BET_UPDATED_EVENT = "efw:cheat:bet-updated";

function stableStringify(value: unknown): string {
	try {
		return JSON.stringify(value);
	} catch {
		return String(value);
	}
}

function areBetPayloadsEqual(a: BetPayload | null, b: BetPayload | null): boolean {
	if (a === b) {
		return true;
	}

	if (!a || !b) {
		return false;
	}

	if (a.bet !== b.bet) {
		return false;
	}

	return stableStringify(a.currencyFormat ?? null) === stableStringify(b.currencyFormat ?? null);
}

export function useCaptainUpHostBridge(
	provider: EngagementProvider | null,
	providerReady: boolean
): void {
	const {
		onBetUpdated,
		onConfigConnection,
		onGameReady,
		onPromotionStarted,
		onPromotionEnded,
		onBuyFeatureStarted,
		onBuyFeatureEnded,
		onPaidRoundStarted,
		onPaidRoundEnded,
		onRoundStarted,
		onRoundEnded,
		onAnimationsStarted,
		onAnimationsEnded,
		sendRewardAcquired,
	} = useCommunicator();

	const seenRewardKeysRef = useRef<Set<string>>(new Set());

	const latestPendingBetRef = useRef<BetPayload | null>(null);
	const lastAppliedBetRef = useRef<BetPayload | null>(null);

	const gameReadyReceivedRef = useRef(false);
	const lastGameReadyProviderRef = useRef<EngagementProvider | null>(null);
	const promotionActiveRef = useRef(false);
	const buyFeatureActiveRef = useRef(false);
	const paidRoundActiveRef = useRef(false);
	const roundActiveRef = useRef(false);
	const animationsActiveRef = useRef(false);

	const providerRef = useRef<EngagementProvider | null>(provider);
	const providerReadyRef = useRef(providerReady);

	useEffect(() => {
		providerRef.current = provider;
	}, [provider]);

	useEffect(() => {
		providerReadyRef.current = providerReady;
	}, [providerReady]);

	const flushPendingBetToProvider = useCallback((source: string): void => {
		const currentProvider = providerRef.current;
		const ready = providerReadyRef.current;
		const pendingBet = latestPendingBetRef.current;

		if (!currentProvider || !ready) {
			debugLog("[CaptainUpHostBridge] bet flush skipped because provider is not ready", {
				source,
				providerReady: ready,
				hasProvider: !!currentProvider,
				hasPendingBet: !!pendingBet,
			});
			return;
		}

		if (!pendingBet) {
			debugLog("[CaptainUpHostBridge] bet flush skipped because no pending bet exists", {
				source,
			});
			return;
		}

		// if (areBetPayloadsEqual(lastAppliedBetRef.current, pendingBet)) {
		// 	debugLog("[CaptainUpHostBridge] bet flush skipped because payload is unchanged", {
		// 		source,
		// 		bet: pendingBet.bet,
		// 		currencyFormat: pendingBet.currencyFormat,
		// 	});
		// 	return;
		// }

		debugLog("[CaptainUpHostBridge] flushing bet to provider", {
			source,
			bet: pendingBet.bet,
			currencyFormat: pendingBet.currencyFormat,
		});

		currentProvider.updateBet(pendingBet.bet, pendingBet.currencyFormat);
		lastAppliedBetRef.current = pendingBet;
	}, []);

	const stageBetForProvider = useCallback(
		(
			source: "ConfigConnection" | "BetUpdated" | "BetUpdatedEvent",
			payload: BetPayload | null
		): void => {
			if (!payload) {
				debugLog(`[CaptainUpHostBridge] ${source} received without usable bet`, {
					payload,
				});
				return;
			}

			latestPendingBetRef.current = payload;

			debugLog(`[CaptainUpHostBridge] staged bet from ${source}`, {
				bet: payload.bet,
				currencyFormat: payload.currencyFormat,
				providerReady: providerReadyRef.current,
				hasProvider: !!providerRef.current,
			});

			flushPendingBetToProvider(source);
		},
		[flushPendingBetToProvider]
	);

	const flushGameReadyToProvider = useCallback((source: string): void => {
		const currentProvider = providerRef.current;
		const ready = providerReadyRef.current;

		if (!gameReadyReceivedRef.current) {
			debugLog("[CaptainUpHostBridge] GameReady flush skipped because host GameReady was not received", {
				source,
				providerReady: ready,
				hasProvider: !!currentProvider,
			});
			return;
		}

		if (!currentProvider || !ready) {
			debugLog("[CaptainUpHostBridge] staging GameReady until provider is ready", {
				source,
				providerReady: ready,
				hasProvider: !!currentProvider,
			});
			return;
		}

		if (lastGameReadyProviderRef.current === currentProvider) {
			debugLog("[CaptainUpHostBridge] GameReady already forwarded for current provider", {
				source,
				providerId: currentProvider.id,
			});
			return;
		}

		debugLog("[CaptainUpHostBridge] forwarding GameReady -> provider", {
			source,
			providerId: currentProvider.id,
		});

		currentProvider.notifyGameReady();
		lastGameReadyProviderRef.current = currentProvider;
	}, []);

	useEffect(() => {
		if (!provider || !providerReady) {
			return;
		}

		debugLog("[CaptainUpHostBridge] subscribing to normalized provider events");

		const unsubscribe = provider.subscribe((event: EngagementProviderEvent) => {
			if (event.type !== "reward_acquired") {
				return;
			}

			if (event.rewardKeys.length === 0) {
				return;
			}

			let emitted = false;

			for (const key of event.rewardKeys) {
				if (seenRewardKeysRef.current.has(key)) {
					continue;
				}

				seenRewardKeysRef.current.add(key);
				emitted = true;
			}

			if (!emitted) {
				return;
			}

			debugLog("[CaptainUpHostBridge] reward acquired -> sending RewardAcquired", {
				rewardCount: event.rewardKeys.length,
			});

			sendRewardAcquired({});
		});

		return () => {
			unsubscribe();
		};
	}, [provider, providerReady, sendRewardAcquired]);

	useSubscription(
		onConfigConnection,
		(payload: MessagePayload<HostToClientMessage, "ConfigConnection">) => {
			stageBetForProvider(
				"ConfigConnection",
				parseBetPayloadFromConfigConnection(payload)
			);
		},
		[stageBetForProvider]
	);

	useSubscription(
		onBetUpdated,
		(payload: MessagePayload<HostToClientMessage, "BetUpdated">) => {
			stageBetForProvider(
				"BetUpdated",
				parseBetPayloadFromBetUpdated(payload)
			);
		},
		[stageBetForProvider]
	);

	useSubscription(
		onGameReady,
		() => {
			gameReadyReceivedRef.current = true;

			debugLog("[CaptainUpHostBridge] host -> GameReady", {
				providerReady: providerReadyRef.current,
				hasProvider: !!providerRef.current,
			});

			flushGameReadyToProvider("GameReady");
		},
		[flushGameReadyToProvider]
	);

	useSubscription(
		onPromotionStarted,
		() => {
			if (promotionActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate PromotionStarted");
				return;
			}

			promotionActiveRef.current = true;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] staging PromotionStarted until provider is ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding PromotionStarted -> provider");
			providerRef.current.notifyPromotionStarted();
		},
		[]
	);

	useSubscription(
		onPromotionEnded,
		() => {
			if (!promotionActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate PromotionEnded");
				return;
			}

			promotionActiveRef.current = false;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] PromotionEnded received before provider was ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding PromotionEnded -> provider");
			providerRef.current.notifyPromotionEnded();
		},
		[]
	);

	useSubscription(
		onBuyFeatureStarted,
		() => {
			if (buyFeatureActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate BuyFeatureStarted");
				return;
			}

			buyFeatureActiveRef.current = true;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] staging BuyFeatureStarted until provider is ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding BuyFeatureStarted -> provider");
			providerRef.current.notifyBuyFeatureStarted();
		},
		[]
	);

	useSubscription(
		onBuyFeatureEnded,
		() => {
			if (!buyFeatureActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate BuyFeatureEnded");
				return;
			}

			buyFeatureActiveRef.current = false;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] BuyFeatureEnded received before provider was ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding BuyFeatureEnded -> provider");
			providerRef.current.notifyBuyFeatureEnded();
		},
		[]
	);

	useSubscription(
		onPaidRoundStarted,
		() => {
			if (paidRoundActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate PaidRoundStarted");
				return;
			}

			paidRoundActiveRef.current = true;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] staging PaidRoundStarted until provider is ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding PaidRoundStarted -> provider");
			providerRef.current.notifyPaidRoundStarted();
		},
		[]
	);

	useSubscription(
		onPaidRoundEnded,
		() => {
			if (!paidRoundActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate PaidRoundEnded");
				return;
			}

			paidRoundActiveRef.current = false;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] staging PaidRoundEnded until provider is ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding PaidRoundEnded -> provider");
			providerRef.current.notifyPaidRoundEnded();
		},
		[]
	);

	useSubscription(
		onRoundStarted,
		() => {
			if (roundActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate RoundStarted");
				return;
			}

			roundActiveRef.current = true;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] staging RoundStarted until provider is ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding RoundStarted -> provider");
			providerRef.current.notifyRoundStarted();
		},
		[]
	);

	useSubscription(
		onRoundEnded,
		() => {
			if (!roundActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate RoundEnded");
				return;
			}

			roundActiveRef.current = false;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] RoundEnded received before provider was ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding RoundEnded -> provider");
			providerRef.current.notifyRoundEnded();
		},
		[]
	);

	useSubscription(
		onAnimationsStarted,
		() => {
			if (animationsActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate AnimationsStarted");
				return;
			}

			animationsActiveRef.current = true;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] staging AnimationsStarted until provider is ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding AnimationsStarted -> provider");
			providerRef.current.notifyAnimationsStarted();
		},
		[]
	);

	useSubscription(
		onAnimationsEnded,
		() => {
			if (!animationsActiveRef.current) {
				debugLog("[CaptainUpHostBridge] skipping duplicate AnimationsEnded");
				return;
			}

			animationsActiveRef.current = false;

			if (!providerRef.current || !providerReadyRef.current) {
				debugLog("[CaptainUpHostBridge] staging AnimationsEnded until provider is ready");
				return;
			}

			debugLog("[CaptainUpHostBridge] forwarding AnimationsEnded -> provider");
			providerRef.current.notifyAnimationsEnded();
		},
		[]
	);

	useEffect(() => {
		const handleBetUpdatedEvent = (event: Event) => {
			const customEvent = event as CustomEvent<unknown>;

			stageBetForProvider(
				"BetUpdatedEvent",
				parseBetPayload(customEvent.detail)
			);
		};

		window.addEventListener(EFW_CHEAT_BET_UPDATED_EVENT, handleBetUpdatedEvent);

		return () => {
			window.removeEventListener(EFW_CHEAT_BET_UPDATED_EVENT, handleBetUpdatedEvent);
		};
	}, [stageBetForProvider]);

	useEffect(() => {
		if (!provider || !providerReady) {
			return;
		}

		flushPendingBetToProvider("ProviderReadyEffect");
		flushGameReadyToProvider("ProviderReadyEffect");
	}, [provider, providerReady, flushPendingBetToProvider, flushGameReadyToProvider]);

	useEffect(() => {
		if (!provider || !providerReady) {
			return;
		}

		if (promotionActiveRef.current) {
			debugLog("[CaptainUpHostBridge] flushing staged PromotionStarted to provider");
			provider.notifyPromotionStarted();
		}
	}, [provider, providerReady]);

	useEffect(() => {
		if (!provider || !providerReady) {
			return;
		}

		if (buyFeatureActiveRef.current) {
			debugLog("[CaptainUpHostBridge] flushing staged BuyFeatureStarted to provider");
			provider.notifyBuyFeatureStarted();
		}
	}, [provider, providerReady]);

	useEffect(() => {
		if (!provider || !providerReady) {
			return;
		}

		if (paidRoundActiveRef.current) {
			debugLog("[CaptainUpHostBridge] flushing staged PaidRoundStarted to provider");
			provider.notifyPaidRoundStarted();
		}
	}, [provider, providerReady]);

	useEffect(() => {
		if (!provider || !providerReady) {
			return;
		}

		if (roundActiveRef.current) {
			debugLog("[CaptainUpHostBridge] flushing staged RoundStarted to provider");
			provider.notifyRoundStarted();
		}
	}, [provider, providerReady]);

	useEffect(() => {
		if (!provider || !providerReady) {
			return;
		}

		if (roundActiveRef.current) {
			debugLog("[CaptainUpHostBridge] flushing staged AnimatinoStarted to provider");
			provider.notifyAnimationsStarted();
		}
	}, [provider, providerReady]);
}

export default useCaptainUpHostBridge;